export type AdRequest = {
	id: string;
	options?: {
		section: string;
	};
	zoneId: number;
	width?: number;
	height?: number;
	callback: (advert: AdResponse, zoneData: AdRequest) => void;
};

export type AdResponse = {
	active: boolean;
	adInfoUrl: string;
	bidIds: { cid: string; crid: string };
	curl?: string;
	data: string; // JSON string that results in AdResponseData
	dbginfo: object;
	dsp: string;
	height: number;
	id: string;
	iframe: 0 | 1;
	impressId?: string;
	requestId: string;
	responsive: 0 | 1;
	tracking: { served: any; visible: any };
	ttl?: number;
	type: 'json' | 'empty' | 'error' | 'native';
	width: number;
	zoneId: number;
	zoneName: string;
};

export type AdResponseData = {
	ads?: AdsJson[]; // present if AdResponse['type'] == 'json'
	native?: AdDataNative; // present if AdResponse['type'] == 'native'
};

export type AdsJson = AdDataAdvert | AdDataCombined | AdDataDrtg | AdDataShop;

export type AdDataAdvert = {
	adImageUrl: string;
	adTag?: string;
	adType: 'ADVERT';
	clickUrl: string;
	companyName: string;
	externalImpress: string | string[];
	longHeadline: string;
	shortHeadline: string;
};

export type AdDataCombined = {
	adImageUrl: string;
	adLogoUrl: string;
	adSquareImageUrl: string;
	adSquareLogoUrl: string;
	adType: 'COMBINED';
	adUrl: string;
	clickUrl: string;
	companyName: string;
	description: string;
	eventTrackers: { impress: string[] };
	faviconUrl: string;
	longHeadline: string;
	shortHeadline: string;
	targetingType: string;
	viewableImpressionUrl: string;
	visibleUrl: string;
};

export type AdDataDrtg = {
	additionalInfo: string;
	adType: 'DRTG';
	category: string;
	clickUrl: string;
	condition: string;
	imageUrl: string;
	manufacturer: string;
	price: string;
	targetingType: string;
	title: string;
	confirmImpUrl?: string;
	viewableImpressionUrl: string;
};

export type AdDataShop = {
	adType: 'SHOP';
	cleanShopUrl: string;
	eventTrackers: { impress: string[] };
	shopFaviconUrl: string;
	shopLogoUrl: string;
	shopName: string;
	shopSlogan: string;
	shopUrl: string;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ASSET_ID_LARGE_IMAGE = 1;
const ASSET_ID_SMALL_IMAGE = 2;
const ASSET_ID_SHORT_TITLE = 11;
const ASSET_ID_LONG_TITLE_IMAGE = 12;

export type AdDataNativeAssets = [
	{
		id: typeof ASSET_ID_LARGE_IMAGE;
		img: {
			url: string;
			w: number;
			h: number;
			ext: {
				appnexus: {
					prevent_crop: 0 | 1;
				};
			};
		};
	},
	{
		id: typeof ASSET_ID_SMALL_IMAGE;
		img: {
			url: string;
			w: number;
			h: number;
			ext: {
				appnexus: {
					prevent_crop: 0 | 1;
				};
			};
		};
	},
	{
		id: typeof ASSET_ID_SHORT_TITLE;
		title: { text: string };
	},
	{
		id: typeof ASSET_ID_LONG_TITLE_IMAGE;
		data: { value: string };
	},
];

export type AdDataNative = {
	assets: AdDataNativeAssets;
	imptrackers?: string[];
	eventTrackers?: {
		event: 1;
		method: 1;
		url: string;
	}[];
	jstracker?: string;
	link: {
		url: string;
	};
};

export type NormalizedAdData = {
	externalImpress?: string[];
	clickUrl: string;
	companyName: string;
	companyNameUrl: string;
	imageUrl: string;
	price?: string;
	title: string;
	mobileTitle: string;
	tag?: string;
	type: 'COMBINED' | 'DRTG' | 'ADVERT' | 'RTB';
	viewableImpressionUrl?: string;
};

function normalizeJsonAd(data: AdsJson[]): undefined | NormalizedAdData {
	const drtgAd = data.find((ad) => ad.adType === 'DRTG');
	const shopAd = data.find((ad) => ad.adType === 'SHOP');
	const combinedAd = data.find((ad) => ad.adType === 'COMBINED');
	const advertAd = data.find((ad) => ad.adType === 'ADVERT');

	if (drtgAd && shopAd) {
		return {
			clickUrl: drtgAd.clickUrl,
			companyName: shopAd.shopName,
			companyNameUrl: shopAd.shopUrl,
			imageUrl: `${drtgAd.imageUrl}?fl=res,350,350,1|jpg,90`,
			price: drtgAd.price,
			title: drtgAd.title,
			mobileTitle: drtgAd.title,
			externalImpress: drtgAd.confirmImpUrl ? [drtgAd.confirmImpUrl] : [],
			viewableImpressionUrl: drtgAd.viewableImpressionUrl,
			type: 'DRTG',
		};
	} else if (combinedAd) {
		return {
			clickUrl: combinedAd.clickUrl,
			companyName: combinedAd.companyName,
			companyNameUrl: combinedAd.clickUrl,
			imageUrl: `${combinedAd.adSquareImageUrl}?fl=res,384,,1|jpg,90`,
			title: combinedAd.longHeadline,
			mobileTitle: combinedAd.shortHeadline,
			viewableImpressionUrl: combinedAd.viewableImpressionUrl,
			type: 'COMBINED',
		};
	} else if (advertAd) {
		return {
			clickUrl: advertAd.clickUrl,
			companyName: advertAd.companyName,
			companyNameUrl: advertAd.clickUrl,
			externalImpress: Array.isArray(advertAd.externalImpress)
				? advertAd.externalImpress
				: [advertAd.externalImpress],

			imageUrl: advertAd.adImageUrl,
			title: advertAd.longHeadline,
			mobileTitle: advertAd.shortHeadline,
			tag: advertAd.adTag,
			type: 'ADVERT',
		};
	}

	return undefined;
}

function normalizeNativeAd(data: AdDataNative): undefined | NormalizedAdData {
	const imageUrl = data.assets.find((asset) => asset.id === ASSET_ID_SMALL_IMAGE)?.img.url;
	const shortTitle = data.assets.find((asset) => asset.id === ASSET_ID_SHORT_TITLE)?.title.text;
	const longTitle = data.assets.find((asset) => asset.id === ASSET_ID_LONG_TITLE_IMAGE)?.data.value;
	// Version 1.1 uses `imptrackers` and 1.2 uses `eventTrackers`
	const externalImpress = data.imptrackers ?? data.eventTrackers?.map((tracker) => tracker.url) ?? [];

	if (!imageUrl || !shortTitle || !longTitle) {
		return undefined;
	}

	return {
		clickUrl: data.link.url,
		companyName: '',
		companyNameUrl: '',
		externalImpress: externalImpress,
		imageUrl: imageUrl,
		mobileTitle: shortTitle,
		price: '',
		tag: '',
		title: longTitle,
		type: 'RTB',
		viewableImpressionUrl: '',
	};
}

export function normalizeAdData(data: AdResponseData): undefined | NormalizedAdData {
	if ('ads' in data) {
		return normalizeJsonAd(data.ads!);
	}
	if ('native' in data) {
		return normalizeNativeAd(data.native!);
	}

	return undefined;
}
